/**
 * NatalWheel — the user's ACTUAL natal chart, drawn from engine longitudes.
 *
 * Previously this was a fixed illustration: twelve zodiac glyphs and seven
 * planet glyphs at hardcoded pixel coordinates, plus four decorative "aspect"
 * lines that connected nothing. It looked like a chart for every user because
 * it was the same picture for every user.
 *
 * Now every mark is computed from `POST /chart`:
 *   - planets sit at their real ecliptic longitude,
 *   - house spokes sit on the real cusps (omitted when the engine did not
 *     return all twelve — no invented cusps),
 *   - aspect chords connect the two planets the aspect actually names,
 *   - the wheel is rotated so the ascendant is on the left, as a chart is read.
 *
 * Honesty rule: when two planets are closer than the glyphs are wide, the
 * GLYPH is nudged apart for legibility but the degree TICK stays at the true
 * longitude, so the drawing never misstates a position.
 */
import type { ReactElement } from "react";

import type { AspectSummary, Planet, WheelGeometry } from "@/lib/api";

const CX = 130;
const CY = 130;

/** Radii, outermost first. */
const R_ZODIAC_OUT = 115;
const R_ZODIAC_IN = 100;
const R_TICK_OUT = 98;
const R_TICK_IN = 92;
const R_GLYPH = 84;
const R_CHORD = 70;
const R_HUB = 20;

/**
 * Minimum angular gap between two planet glyphs before they get nudged. At
 * R_GLYPH this is ~16px of arc, just over one glyph box — enough that a tight
 * stellium (1992 has Uranus/Neptune/Node within a few degrees) stays readable.
 */
const MIN_GLYPH_GAP_DEG = 11;

const SIGN_GLYPHS = [
  "♈",
  "♉",
  "♊",
  "♋",
  "♌",
  "♍",
  "♎",
  "♏",
  "♐",
  "♑",
  "♒",
  "♓",
] as const;

/** Harmonious aspects read cool, hard aspects read warm, conjunction is gold. */
const ASPECT_STROKE: Readonly<Record<AspectSummary["kind"], string>> = {
  trine: "#5BC4D8",
  sextile: "#5BC4D8",
  square: "#D85B5B",
  opposition: "#D85B5B",
  conjunction: "#D4A843",
};

const ASPECT_WIDTH: Readonly<Record<AspectSummary["kind"], number>> = {
  trine: 0.8,
  sextile: 0.55,
  square: 0.8,
  opposition: 0.9,
  conjunction: 0.7,
};

export interface NatalWheelProps {
  readonly planets: readonly Planet[];
  readonly aspects: readonly AspectSummary[];
  readonly wheel: WheelGeometry;
}

/** Normalise any angle into [0, 360). */
function norm(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * Ecliptic longitude → SVG point.
 *
 * Charts are drawn with the ascendant on the left (due west) and longitude
 * increasing counter-clockwise, so the 4th cusp lands at the bottom and the
 * 10th at the top — the orientation every astrologer reads.
 */
function point(longitude: number, ascendant: number, radius: number): { x: number; y: number } {
  const theta = ((180 + (longitude - ascendant)) * Math.PI) / 180;
  return {
    x: CX + radius * Math.cos(theta),
    y: CY - radius * Math.sin(theta),
  };
}

/**
 * Spread glyph angles so neighbours stay readable.
 *
 * Walks the planets in longitude order and pushes any that crowd their
 * predecessor forward by the minimum gap. Returns display angles keyed by
 * planet key; the caller still ticks the true longitude.
 */
function spreadGlyphAngles(
  planets: readonly Planet[],
  ascendant: number,
): ReadonlyMap<string, number> {
  const sorted = [...planets].sort(
    (a, b) => norm(a.longitude - ascendant) - norm(b.longitude - ascendant),
  );
  const out = new Map<string, number>();
  let previous: number | null = null;
  for (const p of sorted) {
    const relative = norm(p.longitude - ascendant);
    const crowded = previous !== null && relative - previous < MIN_GLYPH_GAP_DEG;
    const placed: number = crowded ? (previous as number) + MIN_GLYPH_GAP_DEG : relative;
    out.set(p.key, placed + ascendant);
    previous = placed;
  }
  return out;
}

export function NatalWheel({ planets, aspects, wheel }: NatalWheelProps): ReactElement {
  const asc = wheel.ascendant;
  const glyphAngle = spreadGlyphAngles(planets, asc);
  const byKey = new Map(planets.map((p) => [p.key, p]));
  // Only aspects whose BOTH ends are on the wheel can be drawn as a chord.
  const chords = aspects.filter((a) => byKey.has(a.from) && byKey.has(a.to));

  const label =
    planets.length > 0
      ? `Натальная карта: ${planets.length} планет, ${chords.length} аспектов`
      : "Натальная карта";

  return (
    <svg viewBox="0 0 260 260" role="img" aria-label={label}>
      <title>{label}</title>
      <defs>
        <radialGradient id="nw-g2">
          <stop offset="0" stopColor="#5BC4D8" stopOpacity="0.18" />
          <stop offset="1" stopColor="#5BC4D8" stopOpacity="0" />
        </radialGradient>
      </defs>

      <circle cx={CX} cy={CY} r="120" fill="url(#nw-g2)" />
      <circle cx={CX} cy={CY} r={R_ZODIAC_OUT} fill="none" stroke="#D4A843" strokeWidth="0.6" />
      <circle cx={CX} cy={CY} r={R_ZODIAC_IN} fill="none" stroke="#3A4A7A" strokeWidth="0.4" />
      <circle cx={CX} cy={CY} r={R_CHORD} fill="none" stroke="#3A4A7A" strokeWidth="0.4" />

      {/* Sign boundaries — every 30° of real longitude. */}
      <g stroke="#3A4A7A" strokeWidth="0.4" opacity="0.7">
        {SIGN_GLYPHS.map((_, i) => {
          const a = point(i * 30, asc, R_ZODIAC_IN);
          const b = point(i * 30, asc, R_ZODIAC_OUT);
          return <line key={`sb-${i * 30}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
        })}
      </g>

      {/* Sign glyphs, centred in their 30° sector. */}
      <g
        fontFamily="serif"
        fontSize="9"
        fill="#C9B57E"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {SIGN_GLYPHS.map((glyph, i) => {
          const p = point(i * 30 + 15, asc, (R_ZODIAC_IN + R_ZODIAC_OUT) / 2);
          return (
            <text key={glyph} x={p.x} y={p.y}>
              {glyph}
            </text>
          );
        })}
      </g>

      {/* House cusps — drawn only when the engine returned all twelve. */}
      <g>
        {wheel.houses.map((cusp, i) => {
          const a = point(cusp, asc, R_HUB);
          const b = point(cusp, asc, R_ZODIAC_IN);
          // Angles (1st/4th/7th/10th) carry the chart's frame — draw them solid.
          const isAngle = i % 3 === 0;
          return (
            <line
              key={`cusp-${cusp}`}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke={isAngle ? "#6E7FB5" : "#3A4A7A"}
              strokeDasharray={isAngle ? undefined : "2 3"}
              strokeWidth={isAngle ? 0.8 : 0.45}
              opacity={isAngle ? 0.9 : 0.6}
            />
          );
        })}
      </g>

      {/* Aspect chords between the two planets each aspect actually names. */}
      <g>
        {chords.map((a) => {
          const from = byKey.get(a.from);
          const to = byKey.get(a.to);
          if (!from || !to) return null;
          const p1 = point(from.longitude, asc, R_CHORD);
          const p2 = point(to.longitude, asc, R_CHORD);
          return (
            <line
              key={a.id}
              x1={p1.x}
              y1={p1.y}
              x2={p2.x}
              y2={p2.y}
              stroke={ASPECT_STROKE[a.kind]}
              strokeWidth={ASPECT_WIDTH[a.kind]}
              opacity="0.65"
            />
          );
        })}
      </g>

      {/* Exact-degree ticks — always at the TRUE longitude. */}
      <g stroke="#E8C158" strokeWidth="0.7" opacity="0.85">
        {planets.map((p) => {
          const a = point(p.longitude, asc, R_TICK_OUT);
          const b = point(p.longitude, asc, R_TICK_IN);
          return <line key={`tick-${p.key}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
        })}
      </g>

      {/* Planet glyphs — nudged apart when crowded, never repositioned in truth. */}
      <g
        fontFamily="serif"
        fontSize="12"
        fill="#E8C158"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {planets.map((p) => {
          const at = glyphAngle.get(p.key) ?? p.longitude;
          const g = point(at, asc, R_GLYPH);
          return (
            <g key={p.key}>
              <text x={g.x} y={g.y}>
                {p.glyph}
              </text>
              {p.retrograde ? (
                <text x={g.x + 7} y={g.y + 5} fontSize="6" fill="#D85B5B">
                  R
                </text>
              ) : null}
            </g>
          );
        })}
      </g>

      <circle cx={CX} cy={CY} r="2.5" fill="#D4A843" />
    </svg>
  );
}
